import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Leaf, TreePine, ShieldAlert, ClipboardCheck, Flame, Waves, Wind, Cloud,
  Sparkles, Satellite, Map as MapIcon, BookOpen, Globe, Landmark, Loader2,
  PlayCircle, CheckCircle2, XCircle, FileText, Plus, AlertTriangle, Users,
} from "lucide-react";

const TEAL = "#2E7D6B";        // primary environmental accent
const DEEP = "#0F3D3A";        // executive deep-green
const ALT = "#4FA3D1";
const WARN = "#C99A2E";
const CRIT = "#E67E7E";

// ---------------------------------------------------------------------------
// Section registry — matches backend capability set (16 domains)
// ---------------------------------------------------------------------------
const SECTIONS = [
  { key: "situation_room",     label: "Environmental Situation Room",       icon: ShieldAlert },
  { key: "authorisation_eia",  label: "EIA & Authorisations",               icon: FileText },
  { key: "permit_register",    label: "Environmental Permits",              icon: ClipboardCheck },
  { key: "compliance",         label: "Compliance & Inspections",           icon: ClipboardCheck },
  { key: "incidents",          label: "Incident & Crisis",                  icon: AlertTriangle },
  { key: "protected_areas",    label: "Protected Areas",                    icon: TreePine },
  { key: "natural_capital",    label: "Biodiversity & Natural Capital",     icon: Leaf },
  { key: "water_catchment",    label: "Water & Catchment",                  icon: Waves },
  { key: "pollution_waste",    label: "Pollution, Waste & Emissions",       icon: Wind },
  { key: "climate_resilience", label: "Climate, Carbon & Resilience",       icon: Cloud },
  { key: "remote_sensing",     label: "Monitoring & Remote Sensing",        icon: Satellite },
  { key: "intelligence",       label: "Environmental Intelligence (AI)",    icon: Sparkles },
  { key: "digital_twin_layers",label: "Digital Twin Layers",                icon: MapIcon },
  { key: "knowledge",          label: "Environmental Knowledge",            icon: BookOpen },
  { key: "public_portals",     label: "Public & Professional Portals",      icon: Users },
  { key: "international_reporting", label: "International Reporting",       icon: Globe },
  { key: "cross_pillar",       label: "Cross-Pillar Relationships",         icon: Landmark },
  { key: "acceptance",         label: "Acceptance Audit",                   icon: CheckCircle2 },
];

// ---------------------------------------------------------------------------
// Reusable primitives
// ---------------------------------------------------------------------------
const Card = ({ title, accent = TEAL, children, right, testId }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(15,61,58,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: accent + "25" }}>
      <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
      <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
      <div className="ml-auto">{right}</div>
    </div>
    <div className="p-3 space-y-1">{children}</div>
  </div>
);

const Metric = ({ label, value, accent = TEAL }) => (
  <div className="rounded-sm border px-3 py-2" style={{ borderColor: accent + "30", background: "rgba(7,27,43,0.45)" }}>
    <div className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#7C93A5]">{label}</div>
    <div className="font-display font-bold text-[#F6F8FA] mt-0.5 text-[17px] break-words">
      {typeof value === "boolean" ? (value ? "yes" : "no") : String(value ?? "—")}
    </div>
  </div>
);

const AutoPanel = ({ data, accent = TEAL }) => {
  if (!data) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {Object.entries(data).map(([k, v]) => {
        if (k === "generated_at") return null;
        if (Array.isArray(v)) return (
          <Card key={k} title={k.replace(/_/g, " ")} accent={accent}>
            <div className="space-y-1 max-h-[320px] overflow-y-auto">
              {v.slice(0, 40).map((it, i) => (
                <div key={k + i} className="border-b border-white/5 pb-1 text-[11px] text-[#DCE3E8]">
                  {typeof it === "object" && it !== null ? (
                    <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                      {Object.entries(it).slice(0, 10).map(([kk, vv]) => (
                        <div key={kk} className="truncate">
                          <span className="font-mono text-[10px] text-[#7C93A5]">{kk}</span>{" "}
                          <span className="text-[#F6F8FA]">
                            {typeof vv === "object" ? JSON.stringify(vv) : String(vv)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : String(it)}
                </div>
              ))}
            </div>
          </Card>
        );
        if (typeof v === "object" && v !== null) return (
          <Card key={k} title={k.replace(/_/g, " ")} accent={accent}>
            {Object.entries(v).slice(0, 30).map(([kk, vv]) => (
              <div key={kk} className="border-b border-white/5 pb-1 flex items-start gap-2 text-[12px]">
                <div className="font-mono uppercase tracking-[0.16em] text-[10px] text-[#7C93A5] flex-1 truncate">{kk}</div>
                <div className="font-mono text-[#F6F8FA] break-all text-right max-w-[60%]">
                  {typeof vv === "object" ? JSON.stringify(vv) : String(vv)}
                </div>
              </div>
            ))}
          </Card>
        );
        return <Metric key={k} label={k.replace(/_/g, " ")} value={v} accent={accent} />;
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Situation Room
// ---------------------------------------------------------------------------
const SituationRoom = () => {
  const q = useQuery({ queryKey: ["env-sit"], queryFn: async () => (await api.get("/cadastre360/environment/situation-room")).data });
  if (q.isLoading) return <div className="text-[#B3C2CC] italic p-4">Loading…</div>;
  const d = q.data || {};
  const k = d.cabinet_kpis || {};
  return (
    <div className="space-y-3" data-testid="env-situation">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: TEAL + "60", background: `linear-gradient(120deg, rgba(7,27,43,0.95), rgba(15,61,58,0.75), ${TEAL}25)` }}>
        <ShieldAlert size={32} className="mt-1" style={{ color: TEAL }} />
        <div>
          <div className="font-mono uppercase tracking-[0.32em] text-[10px]" style={{ color: TEAL }}>Environmental Situation Room</div>
          <h2 className="font-display text-2xl font-bold text-[#F6F8FA] mt-1">What environmental risks require attention today?</h2>
          <div className="mt-2 flex items-baseline gap-2">
            <div className="font-display font-bold text-[#F6F8FA] text-[42px] leading-none">{d.environmental_health_index}</div>
            <div className="font-mono uppercase tracking-[0.16em] text-[10px]" style={{ color: TEAL }}>National Environmental Health Index</div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
        <Metric label="Authorisations open" value={k.authorisations_open} />
        <Metric label="Permits active" value={k.permits_active} />
        <Metric label="Permits expiring" value={k.permits_expiring} accent={WARN} />
        <Metric label="Inspections scheduled" value={k.inspections_scheduled} accent={ALT} />
        <Metric label="Findings open" value={k.findings_open} accent={WARN} />
        <Metric label="Active incidents" value={k.incidents_active} accent={CRIT} />
        <Metric label="Protected areas" value={k.protected_areas_registered} />
        <Metric label="Natural capital assets" value={k.natural_capital_assets} />
        <Metric label="Restoration sites" value={k.restoration_sites_active} />
        <Metric label="Carbon projects" value={k.carbon_projects_active} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="Risk heatmap" accent={TEAL}>
          {(d.risk_heatmap || []).map((h) => (
            <div key={h.axis} className="flex items-center gap-2 py-1 text-[12px]">
              <div className="w-40 text-[#F6F8FA]">{h.axis}</div>
              <div className="flex-1 h-1 rounded-sm bg-white/5 relative">
                <div className="absolute inset-y-0 left-0 rounded-sm"
                     style={{ width: `${h.score}%`, background: h.score >= 60 ? CRIT : h.score >= 40 ? WARN : TEAL }} />
              </div>
              <div className="w-12 text-right font-mono text-[11px] text-[#F6F8FA]">{h.score}</div>
            </div>
          ))}
        </Card>
        <Card title="National alerts" accent={CRIT}>
          {(d.national_alerts || []).map((a, i) => (
            <div key={"al" + i} className="border-b border-white/5 py-1 text-[12px]">
              <span className="rounded-sm px-1.5 py-0.5 text-[10px] font-mono uppercase mr-2"
                    style={{ background: (a.severity === "high" ? CRIT : a.severity === "medium" ? WARN : ALT) + "30",
                             color: a.severity === "high" ? CRIT : a.severity === "medium" ? WARN : ALT }}>
                {a.severity}
              </span>
              <span className="text-[#F6F8FA]">{a.message}</span>
              <span className="text-[10.5px] text-[#7C93A5] font-mono ml-2">{a.domain}</span>
            </div>
          ))}
        </Card>
        <Card title="Provincial environmental pressure" accent={TEAL}>
          {(d.provincial_pressure || []).map((p) => (
            <div key={p.province} className="flex items-center gap-2 py-1 text-[12px]">
              <div className="w-40 text-[#F6F8FA] truncate">{p.province}</div>
              <div className="flex-1 h-1 rounded-sm bg-white/5 relative">
                <div className="absolute inset-y-0 left-0 rounded-sm"
                     style={{ width: `${p.pressure_index}%`, background: TEAL }} />
              </div>
              <div className="w-12 text-right font-mono text-[11px] text-[#F6F8FA]">{p.pressure_index}</div>
              <div className="w-24 text-[10px] font-mono uppercase text-[#7C93A5]">{p.top_pressure}</div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Generic list-with-create component (Authorisations, Permits, Inspections, Incidents, Protected Areas)
// ---------------------------------------------------------------------------
const CrudPanel = ({ title, endpoint, testId, fields, mapRow, onCreated }) => {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["env-" + endpoint],
    queryFn: async () => (await api.get(`/cadastre360/environment/${endpoint}`)).data,
  });
  const [form, setForm] = useState(() => Object.fromEntries(fields.map((f) => [f.name, f.default ?? ""])));
  const create = useMutation({
    mutationFn: async () => (await api.post(`/cadastre360/environment/${endpoint}`, form)).data,
    onSuccess: () => {
      toast.success(`${title} created`);
      qc.invalidateQueries({ queryKey: ["env-" + endpoint] });
      if (onCreated) onCreated();
      setForm(Object.fromEntries(fields.map((f) => [f.name, f.default ?? ""])));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Requires officer role"),
  });
  return (
    <div className="space-y-3" data-testid={testId}>
      <Card title={`Create ${title.toLowerCase()}`} accent={TEAL}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {fields.map((f) => (
            <div key={f.name}>
              <div className="font-mono uppercase tracking-[0.16em] text-[10px] text-[#7C93A5] mb-1">{f.label}</div>
              {f.type === "select" ? (
                <select value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                        data-testid={`env-${endpoint}-${f.name}`}
                        className="w-full bg-[#0F3D3A]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA]">
                  {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === "number" ? (
                <input type="number" value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: Number(e.target.value) })}
                       data-testid={`env-${endpoint}-${f.name}`}
                       className="w-full bg-[#0F3D3A]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA] font-mono" />
              ) : (
                <input value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                       placeholder={f.placeholder}
                       data-testid={`env-${endpoint}-${f.name}`}
                       className="w-full bg-[#0F3D3A]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA]" />
              )}
            </div>
          ))}
          <button onClick={() => create.mutate()} disabled={create.isPending}
                  data-testid={`env-${endpoint}-create`}
                  className="md:col-span-2 rounded-sm px-4 py-2 text-[11px] font-mono uppercase tracking-[0.16em] inline-flex items-center justify-center gap-1"
                  style={{ background: TEAL, color: "#0B0F14" }}>
            {create.isPending ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} Create
          </button>
        </div>
      </Card>
      <Card title={`${title} (${(list.data || []).length})`} accent={TEAL}>
        <div className="max-h-[420px] overflow-y-auto space-y-1">
          {(list.data || []).map((row) => (
            <div key={row.id} className="border-b border-white/5 pb-1 text-[11.5px]">
              {mapRow(row)}
            </div>
          ))}
          {(list.data || []).length === 0 && (
            <div className="text-[11px] text-[#7C93A5] italic">No records yet — create one above.</div>
          )}
        </div>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// AI Environmental Brief
// ---------------------------------------------------------------------------
const IntelligencePanel = () => {
  const [audience, setAudience] = useState("cabinet");
  const [focus, setFocus] = useState("");
  const [brief, setBrief] = useState(null);
  const gen = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/environment/intelligence/brief", { audience, focus })).data,
    onSuccess: (d) => { setBrief(d); toast.success("Environmental briefing generated"); },
    onError: () => toast.error("Briefing failed"),
  });
  return (
    <div className="space-y-3" data-testid="env-intelligence">
      <Card title="AI environmental executive briefing" accent={TEAL}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <select value={audience} onChange={(e) => setAudience(e.target.value)}
                  data-testid="env-brief-audience"
                  className="bg-[#0F3D3A]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA]">
            <option value="cabinet">Cabinet</option>
            <option value="minister">Minister — Environment</option>
            <option value="permanent_secretary">Permanent Secretary — Environment</option>
          </select>
          <input value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="Focus (optional)"
                 data-testid="env-brief-focus"
                 className="md:col-span-2 bg-[#0F3D3A]/40 border border-white/10 rounded-sm px-3 py-2 text-[12px] text-[#F6F8FA]" />
          <button onClick={() => gen.mutate()} disabled={gen.isPending}
                  data-testid="env-brief-generate"
                  className="md:col-span-3 rounded-sm px-4 py-2 text-[11px] font-mono uppercase tracking-[0.16em] inline-flex items-center justify-center gap-1"
                  style={{ background: TEAL, color: "#0B0F14" }}>
            {gen.isPending ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} Generate briefing
          </button>
        </div>
      </Card>
      {brief && (
        <Card title={`Briefing · ${brief.engine}`} accent={TEAL}>
          <div className="text-[13px] text-[#F6F8FA] whitespace-pre-wrap leading-relaxed">{brief.brief}</div>
          <div className="mt-2 text-[10.5px] font-mono text-[#7C93A5] border-t border-white/10 pt-2">
            snapshot: {brief.snapshot} · human_approval_required: {String(brief.human_approval_required)}
          </div>
        </Card>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Acceptance
// ---------------------------------------------------------------------------
const AcceptancePanel = () => {
  const qc = useQueryClient();
  const reports = useQuery({ queryKey: ["env-acc"], queryFn: async () => (await api.get("/cadastre360/environment/acceptance/reports")).data });
  const run = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/environment/acceptance/run")).data,
    onSuccess: (d) => { toast.success(`Env acceptance ${d.overall} · ${d.passed}/${d.scope_size}`); qc.invalidateQueries({ queryKey: ["env-acc"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Requires environmental administrator"),
  });
  const latest = (reports.data || [])[0];
  return (
    <div className="space-y-3" data-testid="env-acceptance">
      <Card title="Environmental non-regression audit" accent={TEAL}
             right={
               <button onClick={() => run.mutate()} disabled={run.isPending}
                       data-testid="env-acceptance-run"
                       className="rounded-sm px-3 py-1 text-[10px] font-mono uppercase inline-flex items-center gap-1"
                       style={{ background: TEAL, color: "#0B0F14" }}>
                 {run.isPending ? <Loader2 size={10} className="animate-spin" /> : <PlayCircle size={10} />} Run
               </button>
             }>
        <div className="text-[12px] text-[#B3C2CC]">
          Verifies additive-only implementation and zero regression to Land, Mining, Survey, GSROS and SIC.
        </div>
      </Card>
      {latest && (
        <Card title={`Latest report — ${latest.overall} · ${latest.passed}/${latest.scope_size}`} accent={TEAL}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-[420px] overflow-y-auto">
            {(latest.checks || []).map((c, i) => (
              <div key={`chk-${latest.id}-${i}`} className="border-b border-white/5 pb-1 flex items-start gap-2">
                {c.ok ? <CheckCircle2 size={12} className="text-[#6BB88A] mt-0.5" /> : <XCircle size={12} className="text-[#E67E7E] mt-0.5" />}
                <div>
                  <div className="text-[11px] text-[#F6F8FA]">{c.label}</div>
                  <div className="text-[10px] text-[#7C93A5] font-mono">{c.section} · {c.note}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[10px] font-mono text-[#7C93A5] break-all">signature: {latest.signature}</div>
        </Card>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------
export const EnvironmentalGovernance = () => {
  const [tab, setTab] = useState("situation_room");

  const renderContent = () => {
    if (tab === "situation_room") return <SituationRoom />;
    if (tab === "intelligence")   return <IntelligencePanel />;
    if (tab === "acceptance")     return <AcceptancePanel />;
    if (tab === "authorisation_eia") return (
      <CrudPanel title="Environmental authorisations" endpoint="authorisations" testId="env-authorisations"
        fields={[
          { name: "project_name",    label: "Project name",       placeholder: "e.g. Kariba East Solar" },
          { name: "applicant",       label: "Applicant",          placeholder: "Individual or company" },
          { name: "organisation",    label: "Organisation",       placeholder: "Optional" },
          { name: "activity_type",   label: "Activity",           type: "select", options: ["prospecting", "mining", "processing", "infrastructure", "agriculture", "energy"], default: "prospecting" },
          { name: "category",        label: "Screening category", type: "select", options: ["A", "B", "C"], default: "B" },
          { name: "linked_parcel",   label: "Linked parcel (ref)",placeholder: "Optional" },
          { name: "linked_mining_right", label: "Linked mining right", placeholder: "Optional" },
          { name: "province",        label: "Province",           type: "select", options: ["Harare", "Bulawayo", "Manicaland", "Midlands", "Masvingo", "Mashonaland Central", "Mashonaland East", "Mashonaland West", "Matabeleland North", "Matabeleland South"], default: "Harare" },
          { name: "district",        label: "District",           placeholder: "Optional" },
          { name: "footprint_ha",    label: "Footprint (ha)",     type: "number", default: 0 },
        ]}
        mapRow={(r) => (
          <div>
            <div className="text-[#F6F8FA]">{r.reference} · {r.project_name}</div>
            <div className="text-[10px] font-mono text-[#7C93A5]">{r.activity_type} · Category {r.category} · {r.province} · status {r.status}</div>
          </div>
        )} />
    );
    if (tab === "permit_register") return (
      <CrudPanel title="Environmental permits" endpoint="permits" testId="env-permits"
        fields={[
          { name: "permit_type", label: "Permit type", type: "select",
            options: ["waste_management", "air_emissions", "effluent_discharge", "water_discharge", "hazardous_substances", "noise", "vegetation_clearing", "rehabilitation", "biodiversity_offsets", "carbon_project", "protected_area_activity"],
            default: "waste_management" },
          { name: "holder", label: "Permit holder", placeholder: "Company or individual" },
          { name: "linked_entity_type", label: "Linked entity type", type: "select", options: ["", "mining_right", "parcel", "facility"], default: "" },
          { name: "linked_entity_id",   label: "Linked entity ID",   placeholder: "Optional" },
          { name: "province", label: "Province", type: "select",
            options: ["Harare", "Bulawayo", "Manicaland", "Midlands", "Masvingo", "Mashonaland Central", "Mashonaland East", "Mashonaland West", "Matabeleland North", "Matabeleland South"],
            default: "Harare" },
          { name: "validity_years", label: "Validity (years)", type: "number", default: 3 },
        ]}
        mapRow={(r) => (
          <div>
            <div className="text-[#F6F8FA]">{r.reference} · {r.permit_type}</div>
            <div className="text-[10px] font-mono text-[#7C93A5]">Holder {r.holder} · {r.province} · expires {r.expiry_date} · status {r.status}</div>
          </div>
        )} />
    );
    if (tab === "compliance") return (
      <CrudPanel title="Environmental inspections" endpoint="inspections" testId="env-inspections"
        fields={[
          { name: "site", label: "Site", placeholder: "Site name" },
          { name: "linked_permit_id", label: "Linked permit ID", placeholder: "Optional" },
          { name: "inspection_type", label: "Inspection type", type: "select", options: ["routine", "complaint", "follow_up", "remote"], default: "routine" },
          { name: "officer", label: "Officer", placeholder: "Optional" },
          { name: "province", label: "Province", type: "select",
            options: ["Harare", "Bulawayo", "Manicaland", "Midlands", "Masvingo", "Mashonaland Central", "Mashonaland East", "Mashonaland West", "Matabeleland North", "Matabeleland South"],
            default: "Harare" },
        ]}
        mapRow={(r) => (
          <div>
            <div className="text-[#F6F8FA]">{r.reference} · {r.site}</div>
            <div className="text-[10px] font-mono text-[#7C93A5]">{r.inspection_type} · {r.province} · findings {r.findings_count} · status {r.status}</div>
          </div>
        )} />
    );
    if (tab === "incidents") return (
      <CrudPanel title="Environmental incidents" endpoint="incidents" testId="env-incidents"
        fields={[
          { name: "incident_type", label: "Incident type", type: "select",
            options: ["pollution", "chemical_release", "tailings", "waste", "water_contamination", "wildfire", "flood", "habitat_damage", "illegal_clearing", "wildlife_mortality", "air_quality", "rehabilitation_failure"],
            default: "pollution" },
          { name: "severity", label: "Severity", type: "select", options: ["low", "medium", "high", "critical"], default: "medium" },
          { name: "location_description", label: "Location description", placeholder: "Free text" },
          { name: "linked_entity_id", label: "Linked entity ID", placeholder: "Optional" },
          { name: "responsible_entity", label: "Responsible entity", placeholder: "Optional" },
          { name: "province", label: "Province", type: "select",
            options: ["Harare", "Bulawayo", "Manicaland", "Midlands", "Masvingo", "Mashonaland Central", "Mashonaland East", "Mashonaland West", "Matabeleland North", "Matabeleland South"],
            default: "Harare" },
          { name: "community_impact", label: "Community impact", placeholder: "Optional" },
          { name: "immediate_response", label: "Immediate response", placeholder: "Optional" },
        ]}
        mapRow={(r) => (
          <div>
            <div className="text-[#F6F8FA]">{r.reference} · {r.incident_type}</div>
            <div className="text-[10px] font-mono text-[#7C93A5]">{r.severity} · {r.province} · {r.location_description} · status {r.status}</div>
          </div>
        )} />
    );
    if (tab === "protected_areas") return (
      <CrudPanel title="Protected areas" endpoint="protected-areas" testId="env-protected-areas"
        fields={[
          { name: "name", label: "Name", placeholder: "e.g. Hwange National Park" },
          { name: "designation", label: "Designation", type: "select",
            options: ["national_park", "wetland", "ramsar", "forest_reserve", "conservancy", "heritage_site", "wildlife_corridor", "sensitive_zone", "buffer_zone", "restoration_zone"],
            default: "national_park" },
          { name: "province", label: "Province", type: "select",
            options: ["Harare", "Bulawayo", "Manicaland", "Midlands", "Masvingo", "Mashonaland Central", "Mashonaland East", "Mashonaland West", "Matabeleland North", "Matabeleland South"],
            default: "Matabeleland North" },
          { name: "area_ha", label: "Area (ha)", type: "number", default: 0 },
          { name: "buffer_km", label: "Buffer (km)", type: "number", default: 0 },
        ]}
        mapRow={(r) => (
          <div>
            <div className="text-[#F6F8FA]">{r.name} · {r.designation}</div>
            <div className="text-[10px] font-mono text-[#7C93A5]">{r.province} · {r.area_ha} ha · buffer {r.buffer_km} km</div>
          </div>
        )} />
    );

    // Everything else is a read-only capability panel
    const endpointMap = {
      natural_capital: "natural-capital",
      water_catchment: "water-catchment",
      pollution_waste: "pollution-waste",
      climate_resilience: "climate-resilience",
      remote_sensing: "remote-sensing",
      digital_twin_layers: "digital-twin/layers",
      knowledge: "capabilities",   // reuses Knowledge360 externally
      public_portals: "capabilities",
      international_reporting: "international-reporting",
      cross_pillar: "cross-pillar",
    };
    const ep = endpointMap[tab];
    if (!ep) return null;
    return <ReadOnlyPanel endpoint={ep} tab={tab} />;
  };

  return (
    <div className="p-6 space-y-4" data-testid="page-environmental-governance">
      {/* Hero */}
      <div className="rounded-sm border p-6 flex items-start gap-4 relative overflow-hidden"
           style={{ borderColor: TEAL + "60",
                     background: `linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(15,61,58,0.85) 45%, ${TEAL}30 100%)` }}>
        <Leaf size={38} className="mt-1" style={{ color: TEAL }} />
        <div className="flex-1">
          <div className="font-mono uppercase tracking-[0.32em] text-[10px]" style={{ color: TEAL }}>
            Cadastre360™ · Phase XI · Environmental Governance v1.11
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
            Environmental Governance™ — the fourth sovereign pillar
          </h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-4xl leading-relaxed">
            A peer of Land, Mining and Survey. Consumes existing enterprise services (GIS,
            Digital Twin, AI/LLM, Knowledge, Workflow, Notifications, Digital Trust, Audit)
            without duplicating any engine.
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-[0.16em]">
            <span className="rounded-sm px-2 py-0.5 border" style={{ background: TEAL + "20", borderColor: TEAL + "60", color: TEAL }}>Environmental</span>
            <span className="rounded-sm px-2 py-0.5 border" style={{ background: "#C99A2E20", borderColor: "#C99A2E60", color: "#C99A2E" }}>Strategic</span>
            <span className="rounded-sm px-2 py-0.5 border" style={{ background: "#6BB88A20", borderColor: "#6BB88A60", color: "#6BB88A" }}>Additive</span>
            <span className="rounded-sm px-2 py-0.5 border text-[#B3C2CC] border-white/15">Zero regression</span>
          </div>
        </div>
      </div>

      {/* Section navigation */}
      <div className="rounded-sm border border-white/10 px-2 py-2 flex flex-wrap gap-1 sticky top-2 z-10"
           style={{ background: "rgba(7,27,43,0.85)", backdropFilter: "blur(12px)" }}
           data-testid="env-tabs">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const active = tab === s.key;
          return (
            <button key={s.key} onClick={() => setTab(s.key)}
                    data-testid={"env-tab-" + s.key}
                    className="px-2 py-1 rounded-sm text-[10.5px] font-mono uppercase tracking-[0.12em] inline-flex items-center gap-1.5"
                    style={{ background: active ? TEAL + "40" : "transparent",
                             color: active ? "#F6F8FA" : "#B3C2CC",
                             border: active ? `1px solid ${TEAL}` : "1px solid transparent" }}>
              <Icon size={11} style={{ color: TEAL }} /> {s.label}
            </button>
          );
        })}
      </div>

      {renderContent()}
    </div>
  );
};

const ReadOnlyPanel = ({ endpoint, tab }) => {
  const q = useQuery({
    queryKey: ["env-" + endpoint],
    queryFn: async () => (await api.get(`/cadastre360/environment/${endpoint}`)).data,
  });
  if (q.isLoading) return <div className="text-[#B3C2CC] italic p-4">Loading…</div>;
  return <AutoPanel data={q.data} accent={TEAL} />;
};

export default EnvironmentalGovernance;
